"use client";

import { Search, LayoutGrid, List } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/shared/ui/buttons/button";
import { Input } from "@/shared/ui/inputs/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { ScrollableTagStrip } from "@/shared/ui/inputs/scrollable-tag-strip";

export type ViewMode = "grid" | "list";

interface OrganizationFiltersProps {
  searchInput: string;
  onSearchChange: (value: string) => void;
  filterBy: string;
  onFilterChange: (value: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

const FILTER_VALUES = ["all", "verified", "unverified"] as const;

export const OrganizationFilters = ({
  searchInput,
  onSearchChange,
  filterBy,
  onFilterChange,
  sortBy,
  onSortChange,
  viewMode,
  setViewMode,
}: OrganizationFiltersProps) => {
  const t = useTranslations("Organizations.filters");

  return (
    <div className="mb-6 space-y-3">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
        <div className="relative flex-1 min-w-0 md:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder={t("searchPlaceholder")}
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-9 pl-10"
          />
        </div>

        <div className="flex items-center gap-3 shrink-0 justify-end flex-wrap">
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-[180px] h-9" aria-label={t("sortPlaceholder")}>
              <SelectValue placeholder={t("sortPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">{t("sortName")}</SelectItem>
              <SelectItem value="members">{t("sortMembers")}</SelectItem>
              <SelectItem value="tournaments">{t("sortTournaments")}</SelectItem>
              <SelectItem value="subscribers">{t("sortSubscribers")}</SelectItem>
            </SelectContent>
          </Select>

          <div className="hidden md:flex items-center space-x-1 border border-input rounded-md p-1 h-9 bg-background">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
              aria-label={t("viewGrid")}
              aria-pressed={viewMode === "grid"}
              className="h-7 w-7"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
              aria-label={t("viewList")}
              aria-pressed={viewMode === "list"}
              className="h-7 w-7"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <ScrollableTagStrip ariaLabel={t("filterStripAria")}>
        {FILTER_VALUES.map((v) => (
          <Button
            key={v}
            type="button"
            variant={filterBy === v ? "default" : "outline"}
            size="sm"
            className="shrink-0 rounded-full"
            onClick={() => onFilterChange(v)}
          >
            {t(`filter_${v}`)}
          </Button>
        ))}
      </ScrollableTagStrip>

      {/* Search tag chips (киберспорт/лига/кубок/команда/студия) убраны
          по UX-запросу — не помогали фильтровать, только шумели.
          Локали `searchTags.*` в i18n остаются на случай возврата. */}
    </div>
  );
};
